/**
 * Reporty fee ke stažení (fáze 6) — self-contained HTML k tisku/archivaci.
 * Management fee = přehled fakturace per distributor (F6-B); success fee = stržení
 * per držitel. Mock HTML; v BE (F7) PDF. Žádný externí zdroj.
 */

import { FeeCheck, MgmtFeeRun, SuccessFeeRun } from '../../api/types'

const czk = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 2 })
const units = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 8 })
const pct = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const STYLE = `
  body { font-family: Arial, Helvetica, sans-serif; color: #171A1E; margin: 32px; }
  h1 { font-size: 20px; color: #0A1E3C; margin: 0 0 4px; }
  h2 { font-size: 14px; color: #0A1E3C; text-transform: uppercase; letter-spacing: .5px;
       border-bottom: 2px solid #0A1E3C; padding-bottom: 4px; margin: 24px 0 8px; }
  .sub { color: #5b6470; margin: 0 0 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 8px; }
  th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e3e7ec; }
  th { text-transform: uppercase; font-size: 11px; color: #5b6470; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr.total td { font-weight: 700; border-top: 2px solid #0A1E3C; }
  ul { list-style: none; padding: 0; }
  li { padding: 6px 0; border-bottom: 1px solid #f0f2f5; }
  li.pass strong { color: #2A7E19; } li.fail strong { color: #D02F0B; }
  .detail { color: #5b6470; font-size: 12px; }
  .verdict { display: inline-block; padding: 2px 10px; border-radius: 9999px; font-weight: 700; font-size: 13px; }
  .verdict.ok { background: #e6f4e3; color: #2A7E19; }
  .verdict.err { background: #fbe4df; color: #D02F0B; }
  footer { margin-top: 32px; color: #8a929c; font-size: 11px; }
`

function wrap(title: string, body: string): string {
  return `<!doctype html>
<html lang="cs"><head><meta charset="utf-8"><title>${esc(title)}</title><style>${STYLE}</style></head>
<body>${body}
<footer>Mock report Apex Ops (fáze 6) — v produkci nahradí PDF. Vygenerováno z dat modulu.</footer>
</body></html>`
}

/** HTML sekce kontroly (automatické testy + AI) — přidává se do reportů. */
function checkHtml(check: FeeCheck | undefined, aiTitle: string): string {
  if (!check) return ''
  const allPassed = check.autoTests.length > 0 && check.autoTests.every((t) => t.passed)
  const tests = check.autoTests
    .map(
      (t) => `<li class="${t.passed ? 'pass' : 'fail'}"><strong>${t.passed ? 'PASS' : 'FAIL'}</strong> — ${esc(
        t.name
      )}${t.detail ? `<br><span class="detail">${esc(t.detail)}</span>` : ''}</li>`
    )
    .join('\n')
  const ai = check.aiResults
    .map(
      (r) => `<li class="${r.status === 'OK' ? 'pass' : 'fail'}"><strong>${esc(r.name)} — ${
        r.status === 'OK' ? 'V POŘÁDKU' : 'CHYBA'
      }</strong><br><span class="detail">${esc(r.comment)}</span></li>`
    )
    .join('\n')
  return `
    <h2>Automatické testy — <span class="verdict ${allPassed ? 'ok' : 'err'}">${
    allPassed ? 'VŠE OK' : 'NALEZENY CHYBY'
  }</span></h2>
    <ul>${tests}</ul>
    <h2>${esc(aiTitle)}</h2>
    <ul>${ai}</ul>`
}

function triggerDownload(html: string, fileName: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function buildMgmtFeeReportHtml(run: MgmtFeeRun): string {
  const distRows = run.byDistributor
    .map(
      (d) => `<tr>
        <td>${esc(d.distributorName)}</td>
        <td class="num">${d.clientCount}</td>
        <td class="num">${czk.format(d.totalHoldingValue)} Kč</td>
        <td class="num">${czk.format(d.totalFee)} Kč</td>
        <td class="num">${czk.format(d.totalCommission ?? 0)} Kč</td>
      </tr>`
    )
    .join('\n')
  const lineRows = run.lines
    .map(
      (l) => `<tr>
        <td>${esc(l.clientName)}</td>
        <td>${esc(l.distributorName)}</td>
        <td class="num">${czk.format(l.holdingValue)} Kč</td>
        <td class="num">${pct.format(l.ratePa * 100)} %</td>
        <td class="num">${czk.format(l.fee)} Kč</td>
        <td class="num">${l.commissionRate != null && l.commissionRate > 0 ? pct.format(l.commissionRate * 100) + ' %' : '—'}</td>
        <td class="num">${czk.format(l.commission ?? 0)} Kč</td>
      </tr>`
    )
    .join('\n')
  return wrap(
    `Management fee ${run.period}`,
    `<h1>Management fee — ${esc(run.period)}</h1>
     <p class="sub">Fakturace per distributor · spustil ${esc(run.createdBy)}</p>
     <h2>Souhrn per distributor</h2>
     <table>
       <thead><tr><th>Distributor</th><th class="num">Klientů</th><th class="num">Objem</th><th class="num">Fee / měsíc</th><th class="num">Provize distributora</th></tr></thead>
       <tbody>${distRows}
         <tr class="total"><td>Celkem</td><td class="num"></td><td class="num"></td><td class="num">${czk.format(run.totalFee)} Kč</td><td class="num">${czk.format(run.totalCommission ?? 0)} Kč</td></tr>
       </tbody>
     </table>
     <h2>Rozpad per klient</h2>
     <table>
       <thead><tr><th>Klient</th><th>Distributor</th><th class="num">Objem</th><th class="num">Sazba p.a.</th><th class="num">Fee / měsíc</th><th class="num">Podíl distr.</th><th class="num">Provize</th></tr></thead>
       <tbody>${lineRows}</tbody>
     </table>
     ${checkHtml(run.check, 'AI kontrola (per distributor)')}`
  )
}

export function downloadMgmtFeeReport(run: MgmtFeeRun): void {
  triggerDownload(buildMgmtFeeReportHtml(run), `report-management-fee-${run.period}.html`)
}

/**
 * Report management fee JEN pro jednoho distributora (rozhodnutí Michal 31.7.) — vidí
 * pouze SVÉ klienty, ne ostatní distributory. Posílá se každému zvlášť.
 */
export function buildMgmtFeeDistributorReportHtml(run: MgmtFeeRun, distributorId: string): string {
  const key = distributorId || '—'
  const lines = run.lines.filter((l) => (l.distributorId || '—') === key)
  const distributorName = lines[0]?.distributorName ?? run.byDistributor.find((d) => (d.distributorId || '—') === key)?.distributorName ?? '—'
  const totalFee = lines.reduce((acc, l) => acc + l.fee, 0)
  const totalValue = lines.reduce((acc, l) => acc + l.holdingValue, 0)
  const totalCommission = lines.reduce((acc, l) => acc + (l.commission ?? 0), 0)
  const rows = lines
    .map(
      (l) => `<tr>
        <td>${esc(l.clientName)}</td>
        <td class="num">${czk.format(l.holdingValue)} Kč</td>
        <td class="num">${pct.format(l.ratePa * 100)} %</td>
        <td class="num">${czk.format(l.fee)} Kč</td>
        <td class="num">${l.commissionRate != null ? pct.format(l.commissionRate * 100) + ' %' : '—'}</td>
        <td class="num">${czk.format(l.commission ?? 0)} Kč</td>
      </tr>`
    )
    .join('\n')
  return wrap(
    `Management fee ${run.period} — ${distributorName}`,
    `<h1>Management fee — ${esc(distributorName)}</h1>
     <p class="sub">Období ${esc(run.period)} · pouze vaši klienti</p>
     <table>
       <thead><tr><th>Klient</th><th class="num">Objem</th><th class="num">Sazba p.a.</th><th class="num">Stržené fee</th><th class="num">Váš podíl</th><th class="num">Vaše provize</th></tr></thead>
       <tbody>${rows}
         <tr class="total"><td>Celkem (${lines.length})</td><td class="num">${czk.format(totalValue)} Kč</td><td class="num"></td><td class="num">${czk.format(totalFee)} Kč</td><td class="num"></td><td class="num">${czk.format(totalCommission)} Kč</td></tr>
       </tbody>
     </table>
     <p class="sub">Provize = stržené fee klienta × váš nasmlouvaný podíl; zbytek zůstává fondu.</p>`
  )
}

export function downloadMgmtFeeDistributorReport(run: MgmtFeeRun, distributorId: string, distributorName: string): void {
  const slug = (distributorName || distributorId || 'distributor').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()
  triggerDownload(buildMgmtFeeDistributorReportHtml(run, distributorId), `report-mf-${run.period}-${slug}.html`)
}

export function buildSuccessFeeReportHtml(run: SuccessFeeRun, fundName: string): string {
  const benchmarkPct = ((run.benchmarkPa ?? 0.07) * 100).toFixed(0)
  const rows = run.lines
    .map((l) => {
      const clientRow = `<tr class="total">
        <td>${esc(l.clientName)}</td>
        <td></td>
        <td class="num">${czk.format(l.invested)} Kč</td>
        <td></td>
        <td class="num">${units.format(l.units)}</td>
        <td class="num">${czk.format(l.value)} Kč</td>
        <td></td>
        <td></td>
        <td class="num">${czk.format(l.profit)} Kč</td>
        <td class="num">${czk.format(l.fee)} Kč</td>
        <td class="num">${units.format(l.feeUnits)}</td>
      </tr>`
      const trancheRows = (l.tranches || [])
        .map(
          (t) => `<tr>
        <td class="sub-row">${t.date ? new Date(t.date).toLocaleDateString('cs-CZ') : 'bez data'}</td>
        <td class="num">${t.buyPrice !== null && t.buyPrice !== undefined ? units.format(t.buyPrice) : '—'}</td>
        <td class="num">${czk.format(t.invested)} Kč</td>
        <td class="num">${(t.returnPa * 100).toFixed(2)} %</td>
        <td class="num">${units.format(t.units)}</td>
        <td class="num">${czk.format(t.value)} Kč</td>
        <td class="num">${t.benchmarkMet ? 'ANO' : 'NE'}</td>
        <td class="num">${czk.format(t.profit)} Kč</td>
        <td></td>
        <td class="num">${czk.format(t.fee)} Kč</td>
        <td></td>
      </tr>`
        )
        .join('\n')
      return clientRow + '\n' + trancheRows
    })
    .join('\n')
  return wrap(
    `Success fee ${fundName} ${run.year}`,
    `<h1>Success fee — ${esc(fundName)} ${run.year}</h1>
     <p class="sub">Stav: ${esc(run.status)} · cena konce roku ${units.format(run.yearEndPrice)} · benchmark ${benchmarkPct} % p.a. · spustil ${esc(run.createdBy)}</p>
     <table>
       <thead><tr><th>Držitel / datum investice</th><th class="num">Cena při nákupu</th><th class="num">Investováno</th><th class="num">Zhodnocení p.a.</th><th class="num">Jednotky</th><th class="num">Hodnota k 31.12.</th><th class="num">Benchmark ${benchmarkPct} % splněn</th><th class="num">Zisk vkladu</th><th class="num">Zisk celkem</th><th class="num">Success fee</th><th class="num">Stržené jednotky</th></tr></thead>
       <tbody>${rows}
         <tr class="total"><td>Celkem</td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td class="num">${czk.format(run.totalFee)} Kč</td><td class="num">${units.format(run.totalFeeUnits)}</td></tr>
       </tbody>
     </table>
     <p class="sub">Zisk vkladu se měří od investice, resp. od nejvyšší ceny jednotky z minulých zúčtování (high-water mark). Vklad se zhodnocením pod benchmarkem ${benchmarkPct} % p.a. (Actual/360) success fee neplatí; nad ním se platí sazba třídy z celého zisku vkladu. Stržené jednotky se zaokrouhlují na celé kusy dolů.</p>
     <p class="sub">Podíl distributorů (distribuce): ${czk.format(run.totalDistribution)} Kč</p>
     ${checkHtml(run.check, 'AI kontrola (per klient)')}`
  )
}

export function downloadSuccessFeeReport(run: SuccessFeeRun, fundName: string): void {
  triggerDownload(buildSuccessFeeReportHtml(run, fundName), `report-success-fee-${run.fundKey}-${run.year}.html`)
}
